import { z } from "zod";
import { ServiceType } from "../enums/ServiceType.js";
import { NullableVatRateSchema } from "../invoicing/vatRate.js";
import { MAX_ONLINE_DURATION_MINUTES, refineRdvOnlineDuration } from "../rdv/service.types.js";
/**
 * Runtime contract for the ServiceDescription catalogue endpoints
 * (POST/PUT /api/service-descriptions/). `vatRate` MUST be either NULL
 * (franchise en base) or one of the four canonical French legal rates
 * (2.1, 5.5, 10, 20) — see `VatRateSchema` in @mytour/shared/invoicing.
 * Arbitrary floats (13.37) or out-of-range values (-5, 999) used to
 * land in `service_description.vat_rate` and then snapshot themselves
 * into every quotation/invoice/credit note that uses the description.
 * Now they are rejected at the controller boundary.
 */
// TODO(#366 follow-up): drop the legacy-FE accommodations below once the
// catalogue form is rewritten with React Hook Form + Zod resolver
// (planned with PR3 / #367):
//   - `amount: z.coerce.number()` — the current form serializes the
//     numeric input as a string ("0", "40", …). Once it sends a real
//     number, switch to `z.number()`.
//   - `account: z.number().nullable().optional()` — the current form
//     omits the field when null. Once it always serializes
//     `account: null` explicitly, switch to `z.number().nullable()`.
// Both relaxations only exist because the previous class-validator DTO
// silently tolerated these shapes; tightening them at the same time as
// the Zod migration would have broken every existing catalogue write
// (see CI E2E timeout that surfaced this).
// `vatRate` stays strict (NullableVatRateSchema = NULL or 2.1 / 5.5 /
// 10 / 20) — that's the whole point of this PR's runtime contract and
// the legacy FE never sent it, so no migration burden there.
// RDV en ligne (#429, Q8). Two additive fields, both optional so legacy /
// onboarding writes that omit them stay valid.
//
// IMPORTANT — no `.default(false)` here. `baseShape` is shared by the
// Update schema, and on a PUT an omitted `onlineBookingEnabled` MUST reach
// the service as `undefined` so it preserves the stored value (the service
// reads `onlineBookingEnabled !== undefined` to distinguish "preserve" from
// "explicit toggle"). A `.default(false)` would turn an omitted toggle into
// an explicit `false`, silently disabling an already-online prestation and
// clearing its duration. The create-time default (off) is applied in
// `ServiceDescriptionService.save` (`onlineBookingEnabled ?? false`), not in
// the schema. FR11 (duration mandatory once enabled) is enforced by
// `refineRdvOnlineDuration` below — a missing / non-positive duration is only
// an error when the toggle is explicitly on. `.max` is a sanity ceiling that
// bounds the persisted int (engine-side overflow / abuse guard), not a
// product rule.
const baseShape = {
    label: z.string().min(1),
    amount: z.coerce.number(),
    type: z.nativeEnum(ServiceType),
    account: z.number().nullable().optional(),
    deleted: z.boolean().optional(),
    vatRate: NullableVatRateSchema.optional(),
    onlineBookingEnabled: z.boolean().optional(),
    onlineDurationMinutes: z
        .number()
        .int()
        .positive()
        .max(MAX_ONLINE_DURATION_MINUTES)
        .nullable()
        .optional(),
};
export const CreateServiceDescriptionPayloadSchema = z
    .object(baseShape)
    .superRefine(refineRdvOnlineDuration);
export const UpdateServiceDescriptionPayloadSchema = z
    .object({ id: z.string().min(1) })
    .extend(baseShape)
    .superRefine(refineRdvOnlineDuration);
//# sourceMappingURL=payloads.js.map